/**
 * EthosBoard Pricing page content. Source: Pricing mockup (reviewed Oct 2026).
 * Edit text and numbers here, not in the JSX.
 */

export const PRICE_HERO = {
  eyebrow: 'Pricing',
  title: 'Simple LMS plans that charge only for active users',
  subtitle:
    'Start at $2, Grow at $4 and Scale at $7 per active user per month on annual billing, plus a custom Enterprise plan. An active user is anyone who logs in during the month.',
  reviewed: 'Pricing last reviewed: October 2026 · All prices in USD',
  note: 'Start includes a free 30-day trial. Paid plans are invoiced annually and paid by wire transfer once an order form is signed. Add-ons, integrations and content services are priced on request.',
};

// rate / minU drive the estimator. Enterprise has no rate (custom).
export const PLANS = [
  {
    name: 'Start', tagline: 'For small teams getting started.', price: '$2',
    unit: 'per active user / month, billed annually', min: 'Min. 10 active users · 30 days free',
    cta: 'Start free for 30 days', rate: 2, minU: 10,
    feats: ['LMS and LXP with learning paths and full mobile access', '1 author seat, Level 1 interaction templates', 'Limited AI credits, with top-ups', 'Starter report set and email support'],
  },
  {
    name: 'Grow', tagline: 'For growing teams building training programs.', price: '$4',
    unit: 'per active user / month, billed annually', min: 'Min. 50 active users',
    cta: 'Book a demo', rate: 4, minU: 50,
    feats: ['Everything in Start', 'Personalized learning feed and advanced certifications', '3 author seats, Level 2 scenarios and branching', 'Core skills library and core reports', 'AI Tutor with capped usage'],
  },
  {
    name: 'Scale', popular: true, tagline: 'For organizations proving role readiness.', price: '$7',
    unit: 'per active user / month, billed annually', min: 'Min. 100 active users',
    cta: 'Book a demo', rate: 7, minU: 100,
    feats: ['Everything in Grow', 'Full skills taxonomy: ~250 skills, 6 domains, 5 levels', 'Role Readiness Index, 3 custom role profiles, 1 industry pack', 'Full AI Tutor and adaptive assessment', '10 author seats, API, 1 SSO, 1 HRMS connector'],
  },
  {
    name: 'Enterprise', tagline: 'For regulated, multi-site workforces.', price: 'Custom',
    unit: 'billed annually', min: 'Min. 500 active users',
    cta: 'Talk to us', minU: 500,
    feats: ['Everything in Scale', 'Unlimited authors, role profiles and all 8 industry packs', 'Level 3 simulations, AI role-play, critical-skill gate', 'License-to-Operate and Regulator Evidence Pack', 'Data residency, dedicated CSM and SLA'],
  },
];

export const HOW = {
  eyebrow: 'How EthosBoard pricing works',
  title: 'You pay only for people who log in',
  items: [
    { t: 'You pay only for active users.', d: 'Registered people who never sign in cost nothing, which suits seasonal hiring and onboarding waves.' },
    { t: 'Billing is annual on every plan.', d: 'Predictable costs, paying only for the active users in each month.' },
    { t: 'Plans set the baseline, add-ons go further.', d: 'Any capability outside your plan can be added on request.' },
    { t: 'Third-party licenses stay separate.', d: 'Content library licenses are bought directly from the provider.' },
  ],
};

export const ESTIMATOR = {
  title: 'Estimate your monthly cost',
  label: 'Active users this month',
  min: 10, max: 2000, step: 10, initial: 250,
  enterpriseNote: 'From 500 active users',
  disclaimer:
    'Indicative only. Your final price is confirmed after a short discussion with our team, where we look at your user mix, plan and add-ons and find room to optimize. Billed annually; minimums apply.',
};

export const COMPARE = {
  title: 'Compare plans at a glance',
  subtitle: 'Grow adds personalization and AI Tutor to Start, Scale adds measurable readiness and integrations, and Enterprise adds simulations, regulator evidence and unlimited scale.',
  footnote: '¹ The Role Readiness Index and the critical-skill gate need the full skills taxonomy. Included in every plan: the LMS and LXP with full mobile access, Level 1 interaction templates, the starter report set and email support.',
  columns: ['Start', 'Grow', 'Scale', 'Enterprise'],
  highlight: 2, // Scale column
  rows: [
    ['Price per active user per month', '$2', '$4', '$7', 'Custom'],
    ['Minimum active users', '10', '50', '100', '500+'],
    ['Personalized learning feed', 'Add-on', 'Included', 'Included', 'Included'],
    ['Certifications and renewals', 'Add-on', 'Included', 'Included', 'Included'],
    ['Author seats', '1', '3', '10', 'Unlimited'],
    ['Level 2 interaction templates', 'Add-on', 'Included', 'Included', 'Included'],
    ['Level 3 simulation templates', 'Add-on', 'Add-on', 'Add-on', 'Included'],
    ['AI credits', 'Limited', 'Standard', 'Extended', 'Custom'],
    ['Core skills library', 'Add-on', 'Included', 'Included', 'Included'],
    ['Full skills taxonomy', 'Add-on', 'Add-on', 'Included', 'Included'],
    ['Role Readiness Index¹', 'Add-on', 'Add-on', 'Included', 'Included'],
    ['Critical-skill gate¹', 'Add-on', 'Add-on', 'Add-on', 'Included'],
    ['Custom role profiles', 'Add-on', 'Add-on', '3', 'Unlimited'],
    ['Industry packs', 'Add-on', 'Add-on', '1', 'All 8'],
    ['AI Tutor', 'Add-on', 'Capped', 'Full', 'Full'],
    ['Adaptive assessment', 'Add-on', 'Add-on', 'Included', 'Included'],
    ['AI role-play', 'Add-on', 'Add-on', 'Add-on', 'Included'],
    ['Core reports', 'Add-on', 'Included', 'Included', 'Included'],
    ['Full report catalogue', 'Add-on', 'Add-on', 'Included', 'Included'],
    ['License-to-Operate and Regulator Evidence Pack', 'Add-on', 'Add-on', 'Add-on', 'Included'],
    ['Nationalization workforce reports', 'Add-on', 'Add-on', 'Add-on', 'Included'],
    ['SSO', 'Add-on', 'Add-on', '1 provider', 'All providers'],
    ['API integration', 'Add-on', 'Add-on', 'Included', 'Included'],
    ['HRMS integration', 'Add-on', 'Add-on', '1 pre-built', 'Pre-built and custom'],
    ['Payment gateway', 'Add-on', 'Add-on', 'Add-on', '1 included'],
    ['Content provider linkages', 'Add-on', 'Add-on', 'Add-on', '2 included'],
    ['Interface localization', 'Add-on', 'Add-on', 'Add-on', '1 language'],
    ['Regional data residency', 'Add-on', 'Add-on', 'Add-on', 'Shared region'],
    ['Priority support', 'Add-on', 'Add-on', 'Included', 'Included'],
    ['Dedicated CSM and SLA', 'Add-on', 'Add-on', 'Add-on', 'Included'],
  ],
};

export const ADDONS = {
  eyebrow: 'Add-ons',
  title: 'Go further only where you need to',
  subtitle: 'Add-ons are priced on request. Choose what fits your program and ask for one combined quote.',
  items: [
    { k: 'SSO', d: 'Microsoft Entra ID, Google Workspace, Okta or any SAML or OIDC provider' },
    { k: 'API integration', d: 'Secure keys, a sandbox and developer documentation' },
    { k: 'HRMS integration', d: 'SAP SuccessFactors, Workday, Oracle HCM, Darwinbox, Zoho People, BambooHR, or custom' },
    { k: 'Payment gateway', d: 'Sell courses with Stripe, PayPal, Razorpay or a regional gateway' },
    { k: 'Content provider linkages', d: 'LinkedIn Learning, Coursera, Udemy Business, Go1 and Skillsoft' },
    { k: 'Interface localization', d: 'Arabic with right-to-left layout, Hindi, French, Spanish, Bahasa and more' },
    { k: 'Regional data residency', d: 'Shared regional cloud in the UAE, Saudi Arabia, India, the EU or the US, or a dedicated server' },
  ],
  services: {
    k: 'Content services',
    d: 'Infylearn Technologies builds bespoke courses, simulations and assessments, quoted per project. Available on every plan.',
  },
};

export const CHOOSE = {
  title: 'How to choose the right plan',
  text: 'Pick the plan that matches what you need to prove in the next twelve months, then move up as your program grows.',
  items: [
    { k: 'Choose Start', d: 'if you are launching, replacing a basic system or testing with a small team.' },
    { k: 'Choose Grow', d: 'if you want personalized learning, certifications and AI support.' },
    { k: 'Choose Scale', d: 'if you need to show who is ready for which role, and connect HR and identity systems.' },
    { k: 'Choose Enterprise', d: 'if you run regulated, multi-site operations that need regulator evidence and simulations.' },
  ],
};

export const PILOT = {
  title: 'How a pilot call works',
  steps: [
    { k: 'Discovery call', d: 'We learn about your workforce, goals and current tools.' },
    { k: 'Pilot scope', d: 'We agree a small group of learners, real courses and success measures.' },
    { k: 'Hands-on pilot', d: 'Your team uses EthosBoard with live content while we support you.' },
    { k: 'Readout and quote', d: 'We recommend a plan and add-ons, with a written quote.' },
  ],
  linksBefore: 'Browse ',
  link1: { label: 'client stories', to: '/resources/client-stories' },
  linksMid: ', or read our ',
  link2: { label: 'comparisons', to: '/resources/comparisons' },
};

export const PRICE_FAQS = [
  { q: 'How much does an LMS cost?', a: 'LMS cost depends mainly on the pricing model: per registered user, per active user, flat tiers, or custom enterprise contracts, often with setup fees and paid add-ons. EthosBoard charges per active user: $2, $4 or $7 per active user per month on annual billing, or a custom Enterprise price.' },
  { q: 'How much does EthosBoard cost?', a: 'EthosBoard costs $2 per active user per month on Start, $4 on Grow and $7 on Scale, billed annually. Enterprise is custom.' },
  { q: 'What is an active user?', a: 'An active user is anyone who logs in during the month. You pay only for people who use the platform, not for registered accounts that stay idle.' },
  { q: 'Is there a free trial?', a: 'Yes. Start includes 30 days free. To continue after the trial, you sign an order form and pay annually by wire transfer at $2 per active user per month.' },
  { q: 'Is there a minimum number of users?', a: 'Yes. The minimum is 10 active users on Start, 50 on Grow, 100 on Scale and 500 or more on Enterprise.' },
  { q: 'What is the difference between Start, Grow, Scale and Enterprise?', a: 'Start covers core learning delivery for small teams. Grow adds personalization, certifications and AI Tutor. Scale adds the full skills taxonomy, Role Readiness Index and integrations. Enterprise adds simulations, regulator evidence and unlimited scale.' },
  { q: 'What are add-ons and how are they priced?', a: 'Add-ons are capabilities outside your plan, such as SSO, HRMS integration, industry packs or data residency. They are priced on request, and we send one combined quote.' },
  { q: 'Are third-party content licenses included?', a: 'No. You can link libraries such as LinkedIn Learning, Coursera, Udemy Business, Go1 and Skillsoft, but you buy those licenses from the provider.' },
  { q: 'Can you build the courses for us?', a: 'Yes. Our instructional design team builds custom courses, simulations and assessments, quoted per project.' },
  { q: 'Do you offer unlimited users or named-user licensing?', a: 'Yes. Ask about an organization-wide unlimited license or named-user licensing, and we will tailor a quote.' },
  { q: 'Can we change plans later?', a: 'Yes. You can move up a plan as your program grows, and your content, learners and history stay in place.' },
];

export const PRICE_CTA = {
  title: 'See EthosBoard working with your own content',
  text: 'Start free for 30 days, or book a demo and we will walk you through the right plan, show EthosAI in action and outline a pilot for your team.',
  primary: 'Start free for 30 days',
  secondary: 'Book a demo',
};